import { formatHeroRingValue } from "../formatHeroRingValue";
import { loadImageAsset } from "./loadImageAsset";
import { loadProfileImageForCanvas } from "./loadProfileImageForCanvas";

import circleRing from "../../assets/media/icons/Circle.png";
import framePng from "../../assets/media/border-style.png";
import pnlCharacters from "../../assets/media/PNL-Page-Characters.png";
import ethLogo from "../../assets/media/ETH-Logo-Medium.png";
import defaultAvatar from "../../assets/media/icons/NavLogoGold.png";
import pageBackground from "../../assets/media/images/background pl.webp";
import starGold from "../../assets/media/SingularStarGold.png";

/** Website hero-circle reference diameter (px). */
const REF_CIRCLE = 550;
/** < 1 zooms the circle out vs the gold frame, revealing more background margin. */
const CIRCLE_ZOOM = 0.95;
const CIRCLE_D = 1000 * CIRCLE_ZOOM;
const SCALE = CIRCLE_D / REF_CIRCLE;
const s = (px) => px * SCALE;

const CANVAS_W = 1200;
const CANVAS_H = 1320;

const GREEN = "#28ef55";
const GREEN_BRIGHT = "#39ff67";
const RED = "#ff002a";

const ETH_FILTER_GREEN =
  "brightness(0) saturate(100%) invert(63%) sepia(77%) saturate(2020%) hue-rotate(95deg) brightness(101%) contrast(99%)";
const ETH_FILTER_WHITE = "brightness(0) invert(1)";

/** Matches .hero-yourpnl-roi-chip on the Your PNL tab. */
const ROI_CHIP = {
  fontSize: 1.18 * 16,
  padTop: 0.12 * 16,
  padRight: 0.58 * 16,
  padBottom: 0.12 * 16,
  padLeft: 0.48 * 16,
};

/** Stats bar — matches .hero-stats-grid / .hero-stat-item at REF_CIRCLE. */
const STATS_BAR = {
  height: 33,
  fontSize: 11.5,
  gap: 3.5,
};

/** Matches .hero-stat-brand-img--* on the Your PNL tab. */
const BRAND_ICON_SIZES = {
  // Wide wordmark (5:1); maxWidth must allow ~22px rendered height to match peer logos.
  sandisk: { height: 28, maxWidth: 56, offsetY: 0 },
  // PNG has extra transparent space above the mark; offsetY fine-tunes after alpha trim.
  pokemon: { height: 22, maxWidth: 100, offsetY: 0 },
  gold: { height: 22, maxWidth: 44, offsetY: 0 },
};

/** Vertical spacing from Your PNL tab CSS (16px rem base, scaled via s()). */
const LAYOUT = {
  profileBlockTop: 10,
  profileBlockLift: 85,
  avatarTranslateY: 12,
  profileFlexGap: 6,
  usernameGapAfterAvatar: 12,
  usernameFontSize: 10,
  statusMarginTop: 0.5 * 16,
  statusFontSize: 14,
  statusLineHeight: 1.3,
  contentPanelTop: 7.85 * 16,
  headRowMarginTop: 0.55 * 16,
  headRowMarginBottom: 0.55 * 16,
  statsFootMarginTop: 0.1 * 16,
  statsGridMarginTop: 0.08 * 16,
  captionMarginTop: 10,
  captionPaddingTop: 0.58 * 16,
  headRowGap: 0.6 * 16,
  headRowInnerGap: 0.58 * 16,
};

/** Matches .hero-yourpnl-stats-caption (upper clamp ~0.92rem). */
const STATS_CAPTION = {
  fontSize: 14,
  fontWeight: 500,
};

async function ensureFonts() {
  if (!document.fonts?.load) return;
  await Promise.all([
    document.fonts.load(`700 ${s(10)}px "Urbanist"`),
    document.fonts.load(`500 ${s(14)}px "Urbanist"`),
    document.fonts.load(`600 ${s(14)}px "Urbanist"`),
    document.fonts.load(`700 ${s(14)}px "Urbanist"`),
    document.fonts.load(`900 ${s(24.8)}px "Urbanist"`),
    document.fonts.load(`800 ${s(ROI_CHIP.fontSize)}px "Urbanist"`),
    document.fonts.load(`600 ${s(STATS_BAR.fontSize)}px "Urbanist"`),
    document.fonts.load(`800 ${s(STATS_BAR.fontSize)}px "Urbanist"`),
    document.fonts.load(`500 ${s(STATS_CAPTION.fontSize)}px "Urbanist"`),
    document.fonts.load(`500 ${s(68)}px "Righteous"`),
  ]).catch(() => {});
}

function formatMetricValue(value) {
  const n = Math.round(Number(value)) || 0;
  return `$${n.toLocaleString("en-US")}`;
}

function formatRoi(roiPct) {
  const n = Number(roiPct);
  if (!Number.isFinite(n)) return "+0%";
  const prefix = n > 0 ? "+" : "";
  return `${prefix}${Math.round(n)}%`;
}

/** Gold frame overlay (transparent center) — drawn last over the full card. */
function drawBorderFrame(ctx, frameImg) {
  if (!frameImg?.naturalWidth) return;
  ctx.drawImage(frameImg, 0, 0, CANVAS_W, CANVAS_H);
}

function drawPageBackground(ctx, bgImg) {
  ctx.fillStyle = "#140e0e";
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  if (!bgImg?.naturalWidth) return;

  // Wide artwork: clouds/mountains live on the left & right edges.
  // Center-crop (cover) hides them; pin both sides like the site's portrait framing.
  const scale = CANVAS_H / bgImg.height;
  const dw = bgImg.width * scale;
  const dh = CANVAS_H;

  ctx.drawImage(bgImg, 0, 0, dw, dh);
  ctx.drawImage(bgImg, CANVAS_W - dw, 0, dw, dh);
}

/** Wallpaper inside the hero circle — matches transparent .hero-circle-inner on the site. */
function drawCircleWallpaper(ctx, bgImg, cx, cy, radius) {
  if (!bgImg?.naturalWidth) return;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.clip();

  const scale = CANVAS_H / bgImg.height;
  const dw = bgImg.width * scale;
  ctx.drawImage(bgImg, 0, 0, dw, CANVAS_H);
  ctx.drawImage(bgImg, CANVAS_W - dw, 0, dw, CANVAS_H);
  ctx.restore();
}

/** Exterior halo at gold ring edge — drawn on top, transparent inside. */
function drawCircleRedGlow(ctx, cx, cy, radius) {
  const blur = s(30);
  const peak = 0.702;
  // Ring asset sits inset -10px on the site; glow hugs that outer edge.
  const glowInner = radius + s(10);

  ctx.save();
  const grad = ctx.createRadialGradient(cx, cy, glowInner, cx, cy, glowInner + blur);
  grad.addColorStop(0, "rgba(255, 0, 0, 0)");
  grad.addColorStop(0.1, `rgba(255, 0, 0, ${peak * 0.55})`);
  grad.addColorStop(0.4, `rgba(255, 0, 0, ${peak * 0.28})`);
  grad.addColorStop(0.75, `rgba(255, 0, 0, ${peak * 0.1})`);
  grad.addColorStop(1, "rgba(255, 0, 0, 0)");

  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, glowInner + blur, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawCharacters(ctx, img, cx, cy, radius) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.clip();

  if (img?.naturalWidth) {
    ctx.save();
    ctx.filter = "saturate(1.12) contrast(1.05)";
    const scale = Math.max((radius * 2) / img.width, (radius * 2) / img.height);
    const dw = img.width * scale;
    const dh = img.height * scale;
    ctx.drawImage(img, cx - dw / 2, cy + radius - dh, dw, dh);
    ctx.restore();
  }

  ctx.restore();
}

function drawRing(ctx, ringImg, cx, cy, radius) {
  const inset = s(10);
  const ringSize = (radius + inset) * 2;
  if (ringImg?.naturalWidth) {
    ctx.drawImage(ringImg, cx - ringSize / 2, cy - ringSize / 2, ringSize, ringSize);
    return;
  }
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.strokeStyle = "#d4af37";
  ctx.lineWidth = s(8);
  ctx.stroke();
}

function drawAvatar(ctx, img, cx, top, size) {
  const r = size / 2;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, top + r, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (img?.naturalWidth) {
    ctx.drawImage(img, cx - r, top, size, size);
  } else {
    ctx.fillStyle = "#1a1212";
    ctx.fillRect(cx - r, top, size, size);
  }
  ctx.restore();
}

function fillTextGlow(ctx, text, x, y, layers, fillStyle = "#ffffff") {
  ctx.save();
  ctx.fillStyle = fillStyle;
  for (const layer of layers) {
    ctx.shadowColor = layer.color;
    ctx.shadowBlur = layer.blur ?? 0;
    ctx.shadowOffsetX = layer.offsetX ?? 0;
    ctx.shadowOffsetY = layer.offsetY ?? 0;
    ctx.fillText(text, x, y);
  }
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.fillText(text, x, y);
  ctx.restore();
}

const STATUS_ACCENT_GLOW = [
  { blur: s(6), color: "rgba(255, 230, 140, 0.85)" },
  { blur: s(14), color: "rgba(255, 200, 80, 0.65)" },
  { blur: s(22), color: "rgba(255, 170, 50, 0.4)" },
  { blur: s(34), color: "rgba(255, 150, 40, 0.18)" },
  { blur: 0, color: "rgba(0, 0, 0, 0.82)", offsetY: s(1) },
];

const STATUS_SEP_GLOW = [
  { blur: s(8), color: "rgba(255, 210, 120, 0.35)" },
  { blur: 0, color: "rgba(0, 0, 0, 0.75)", offsetY: s(1) },
];

function drawAccentText(ctx, text, x, y) {
  fillTextGlow(ctx, text, x, y, STATUS_ACCENT_GLOW);
}

function drawStatusPlainText(ctx, text, x, y) {
  ctx.save();
  ctx.fillStyle = "#ffffff";
  ctx.fillText(text, x, y);
  ctx.restore();
}

function drawStatusSepText(ctx, text, x, y) {
  fillTextGlow(ctx, text, x, y, STATUS_SEP_GLOW, "rgba(255, 255, 255, 0.72)");
}

function measureStatusPartWidth(ctx, part, statusFontSize) {
  if (part.accent) {
    ctx.font = `700 ${statusFontSize}px "Urbanist", sans-serif`;
    ctx.letterSpacing = `${s(0.42)}px`;
  } else if (part.sep) {
    ctx.font = `500 ${statusFontSize}px "Urbanist", sans-serif`;
    ctx.letterSpacing = "0px";
  } else {
    ctx.font = `500 ${statusFontSize}px "Urbanist", sans-serif`;
    ctx.letterSpacing = "0px";
  }
  const width = ctx.measureText(part.text).width;
  ctx.letterSpacing = "0px";
  return width;
}

function layoutStatusParts(ctx, parts, cx, statusFontSize, starSize) {
  const widths = parts.map((part) => measureStatusPartWidth(ctx, part, statusFontSize));
  const totalW = widths.reduce((a, b) => a + b, 0) + starSize + s(4);
  let x = cx - totalW / 2;
  return parts.map((part, index) => {
    const item = { part, x, width: widths[index] };
    x += widths[index];
    return item;
  });
}

function drawStatusLine(ctx, parts, cx, statusY, statusFontSize, starImg, starSize) {
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  const layout = layoutStatusParts(ctx, parts, cx, statusFontSize, starSize);

  layout.forEach(({ part, x }) => {
    if (part.accent) return;
    if (part.sep) {
      ctx.font = `500 ${statusFontSize}px "Urbanist", sans-serif`;
      drawStatusSepText(ctx, part.text, x, statusY);
    } else {
      ctx.font = `500 ${statusFontSize}px "Urbanist", sans-serif`;
      drawStatusPlainText(ctx, part.text, x, statusY);
    }
  });

  layout.forEach(({ part, x }) => {
    if (!part.accent) return;
    ctx.font = `700 ${statusFontSize}px "Urbanist", sans-serif`;
    ctx.letterSpacing = `${s(0.42)}px`;
    drawAccentText(ctx, part.text, x, statusY);
    ctx.letterSpacing = "0px";
  });

  const last = layout[layout.length - 1];
  const starX = last.x + last.width + s(4);
  if (starImg?.naturalWidth) {
    ctx.save();
    ctx.filter =
      "drop-shadow(0 0 6px rgba(255, 215, 120, 0.55)) drop-shadow(0 0 12px rgba(255, 180, 60, 0.35))";
    ctx.drawImage(starImg, starX, statusY + s(1), starSize, starSize);
    ctx.restore();
  }
}

function fitImageInBox(naturalW, naturalH, maxW, maxH) {
  const scale = Math.min(maxW / naturalW, maxH / naturalH);
  return {
    w: naturalW * scale,
    h: naturalH * scale,
  };
}

/** Draw image like CSS object-fit: contain inside a fixed box. */
function drawContainedImage(ctx, img, slotX, slotY, maxW, maxH, filter) {
  if (!img?.naturalWidth) return;
  const { w, h } = fitImageInBox(img.naturalWidth, img.naturalHeight, maxW, maxH);
  const drawX = slotX + (maxW - w) / 2;
  const drawY = slotY + (maxH - h) / 2;
  ctx.save();
  if (filter) ctx.filter = filter;
  ctx.drawImage(img, drawX, drawY, w, h);
  ctx.restore();
}

function drawStackedTitle(ctx, titleRightX, titleTop) {
  ctx.save();
  ctx.font = `900 ${s(24.8)}px "Urbanist", sans-serif`;
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillStyle = "#faf6f2";
  ctx.shadowColor = "rgba(170, 36, 28, 0.42)";
  ctx.shadowBlur = s(20);
  ctx.fillText("NFT", titleRightX, titleTop);
  ctx.fillText("PNL", titleRightX, titleTop + s(25.2));
  ctx.restore();
}

function measureHeadRowLayout(ctx, pnlValue, roiText) {
  const titleFontSize = s(24.8);
  const titleLineGap = s(25.2);
  const titleHeight = titleLineGap + titleFontSize * 0.85;
  const valueFontSize = s(68);
  const ethW = s(42);
  const ethH = s(51);
  const headGap = s(LAYOUT.headRowGap);
  const innerGap = s(LAYOUT.headRowInnerGap);

  ctx.font = `900 ${titleFontSize}px "Urbanist", sans-serif`;
  const titleW = Math.max(ctx.measureText("NFT").width, ctx.measureText("PNL").width);
  ctx.font = `500 ${valueFontSize}px "Righteous", cursive`;
  const valueW = ctx.measureText(pnlValue).width;
  const roiMetrics = measureRoiChip(ctx, roiText);
  const { w: roiW, h: roiChipH } = roiMetrics;

  const rowHeight = Math.max(titleHeight, valueFontSize, ethH, roiChipH);

  return {
    titleW,
    titleHeight,
    valueW,
    valueFontSize,
    ethW,
    ethH,
    roiW,
    roiChipH,
    headGap,
    innerGap,
    rowHeight,
  };
}

function drawHeadRow(ctx, {
  cx,
  rowTop,
  pnlValue,
  roiText,
  pnlNeg,
  roiNeg,
  ethImg,
}) {
  const layout = measureHeadRowLayout(ctx, pnlValue, roiText);
  const {
    titleW,
    titleHeight,
    valueW,
    valueFontSize,
    ethW,
    ethH,
    roiW,
    headGap,
    innerGap,
    rowHeight,
  } = layout;

  const rowCenterY = rowTop + rowHeight / 2;
  const totalHeadW = titleW + headGap + valueW + innerGap + ethW + innerGap + roiW;
  const headStartX = cx - totalHeadW / 2;
  const titleRightX = headStartX + titleW;
  const valueX = headStartX + titleW + headGap;
  const titleTop = rowCenterY - titleHeight / 2;

  drawStackedTitle(ctx, titleRightX, titleTop);

  ctx.font = `500 ${valueFontSize}px "Righteous", cursive`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = pnlNeg ? RED : GREEN;
  ctx.fillText(pnlValue, valueX, rowCenterY);

  if (ethImg?.naturalWidth) {
    drawContainedImage(
      ctx,
      ethImg,
      valueX + valueW + innerGap,
      rowCenterY - ethH / 2,
      ethW,
      ethH,
      pnlNeg ? ETH_FILTER_WHITE : ETH_FILTER_GREEN
    );
  }

  drawRoiChip(
    ctx,
    roiText,
    valueX + valueW + innerGap + ethW + innerGap,
    rowCenterY,
    roiNeg
  );

  return rowTop + rowHeight;
}

const roiTextInkCache = new Map();

function applyRoiChipFont(ctx, fontSize) {
  ctx.font = `800 ${fontSize}px "Urbanist", sans-serif`;
  ctx.letterSpacing = `${fontSize * 0.02}px`;
}

/** Pixel-accurate ink bounds so glyphs like % sit centered in the pill. */
function measureRoiTextInk(text, fontSize) {
  const key = `${text}|${fontSize}`;
  const cached = roiTextInkCache.get(key);
  if (cached) return cached;

  const scratch = document.createElement("canvas");
  const sctx = scratch.getContext("2d");
  if (!sctx) {
    return {
      textWidth: fontSize,
      inkHeight: fontSize,
      baselineToInkTop: -fontSize * 0.72,
      baselineToInkBottom: fontSize * 0.28,
    };
  }

  applyRoiChipFont(sctx, fontSize);
  const metrics = sctx.measureText(text);
  const textWidth = metrics.width;
  const ascent =
    metrics.actualBoundingBoxAscent > 0 ? metrics.actualBoundingBoxAscent : fontSize * 0.72;
  const pad = Math.ceil(fontSize * 0.2);
  scratch.width = Math.ceil(textWidth) + pad * 2;
  scratch.height = Math.ceil(fontSize * 1.45) + pad * 2;

  applyRoiChipFont(sctx, fontSize);
  sctx.textBaseline = "alphabetic";
  const baselineY = pad + ascent;
  sctx.fillStyle = "#fff";
  sctx.fillText(text, pad, baselineY);

  const { data, width, height } = sctx.getImageData(0, 0, scratch.width, scratch.height);
  let top = height;
  let bottom = -1;

  for (let py = 0; py < height; py++) {
    for (let px = 0; px < width; px++) {
      if (data[(py * width + px) * 4 + 3] > 8) {
        if (py < top) top = py;
        if (py > bottom) bottom = py;
      }
    }
  }

  const fallback = {
    textWidth,
    inkHeight: fontSize,
    baselineToInkTop: -ascent,
    baselineToInkBottom: fontSize - ascent,
  };

  if (bottom < top) {
    roiTextInkCache.set(key, fallback);
    return fallback;
  }

  const result = {
    textWidth,
    inkHeight: bottom - top + 1,
    baselineToInkTop: top - baselineY,
    baselineToInkBottom: bottom - baselineY,
  };
  roiTextInkCache.set(key, result);
  return result;
}

function measureRoiChip(ctx, text) {
  const fontSize = s(ROI_CHIP.fontSize);
  const padLeft = s(ROI_CHIP.padLeft);
  const padRight = s(ROI_CHIP.padRight);
  const padTop = s(ROI_CHIP.padTop);
  const padBottom = s(ROI_CHIP.padBottom);
  const ink = measureRoiTextInk(text, fontSize);
  const minH = padTop + fontSize + padBottom;
  const h = Math.max(minH, padTop + ink.inkHeight + padBottom);

  return {
    w: ink.textWidth + padLeft + padRight,
    h,
    fontSize,
    padLeft,
    padTop,
    padBottom,
    ink,
  };
}

function drawRoiChip(ctx, text, x, y, negative) {
  const fontSize = s(ROI_CHIP.fontSize);
  const padLeft = s(ROI_CHIP.padLeft);
  const padRight = s(ROI_CHIP.padRight);
  const padTop = s(ROI_CHIP.padTop);
  const padBottom = s(ROI_CHIP.padBottom);
  const ink = measureRoiTextInk(text, fontSize);
  const w = ink.textWidth + padLeft + padRight;
  const minH = padTop + fontSize + padBottom;
  const h = Math.max(minH, padTop + ink.inkHeight + padBottom);
  const rx = x;
  const ry = y - h / 2;
  const borderColor = negative ? RED : GREEN_BRIGHT;
  const fillColor = negative ? RED : GREEN_BRIGHT;
  const bg = negative ? "rgba(24, 8, 8, 0.45)" : "rgba(8, 18, 12, 0.45)";

  ctx.save();
  ctx.shadowColor = negative ? "rgba(255, 92, 92, 0.35)" : "rgba(57, 255, 103, 0.35)";
  ctx.shadowBlur = s(6);
  ctx.beginPath();
  ctx.roundRect(rx, ry, w, h, h / 2);
  ctx.fillStyle = bg;
  ctx.fill();
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();

  applyRoiChipFont(ctx, fontSize);
  ctx.fillStyle = fillColor;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  const pillCenterY = ry + h / 2;
  const inkMidFromBaseline = (ink.baselineToInkTop + ink.baselineToInkBottom) / 2;
  const textY = pillCenterY - inkMidFromBaseline;

  if (!negative) {
    ctx.shadowColor = "rgba(57, 255, 103, 0.45)";
    ctx.shadowBlur = s(10);
  } else {
    ctx.shadowColor = "rgba(255, 92, 92, 0.45)";
    ctx.shadowBlur = s(10);
  }
  ctx.fillText(text, rx + padLeft, textY);
  ctx.shadowBlur = 0;
  ctx.letterSpacing = "0px";
  return w;
}

const alphaTrimCache = new WeakMap();

/** Source-pixel offset of visible content center from image box center (positive = content sits low). */
function getImageAlphaTrimOffsetY(img) {
  if (!img?.naturalWidth) return 0;
  const cached = alphaTrimCache.get(img);
  if (cached !== undefined) return cached;

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return 0;

  ctx.drawImage(img, 0, 0);
  const { data, width, height } = ctx.getImageData(0, 0, img.naturalWidth, img.naturalHeight);
  let top = height;
  let bottom = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 10) {
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }

  if (bottom < top) {
    alphaTrimCache.set(img, 0);
    return 0;
  }

  const contentCenterY = top + (bottom - top + 1) / 2;
  const trimOffset = contentCenterY - height / 2;
  alphaTrimCache.set(img, trimOffset);
  return trimOffset;
}

function measureBrandIcon(img, brandKey) {
  const spec = BRAND_ICON_SIZES[brandKey] || { height: 24, maxWidth: 76, offsetY: 0 };
  const maxW = s(spec.maxWidth);
  const maxH = s(spec.height);
  const aspect = img.width / img.height;

  let iconW = maxH * aspect;
  let iconH = maxH;

  if (iconW > maxW) {
    iconW = maxW;
    iconH = maxW / aspect;
  }

  const trimOffsetY = getImageAlphaTrimOffsetY(img);
  const visualOffsetY = (trimOffsetY / img.naturalHeight) * iconH;

  return { w: iconW, h: iconH, offsetY: s(spec.offsetY) - visualOffsetY };
}

function drawStatDivider(ctx, x, y, barH) {
  const top = y + barH * 0.06;
  const bottom = y + barH * 0.94;
  const grad = ctx.createLinearGradient(x, top, x, bottom);
  grad.addColorStop(0, "rgba(255, 255, 255, 0.12)");
  grad.addColorStop(0.22, "rgba(255, 255, 255, 0.82)");
  grad.addColorStop(0.5, "rgba(255, 255, 255, 1)");
  grad.addColorStop(0.78, "rgba(255, 255, 255, 0.78)");
  grad.addColorStop(1, "rgba(255, 255, 255, 0.1)");
  ctx.strokeStyle = grad;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, top);
  ctx.lineTo(x, bottom);
  ctx.stroke();
}

function drawStatsBar(ctx, metrics, metricImages, cx, y, barWidth) {
  const statFontSize = s(STATS_BAR.fontSize);
  const statGap = s(STATS_BAR.gap);
  const barH = s(STATS_BAR.height);
  const barX = cx - barWidth / 2;
  const colW = barWidth / 3;

  ctx.save();

  ctx.beginPath();
  ctx.rect(barX, y, barWidth, barH);
  ctx.clip();

  const bgGrad = ctx.createLinearGradient(barX, y, barX, y + barH);
  bgGrad.addColorStop(0, "rgba(255, 20, 20, 0.06)");
  bgGrad.addColorStop(0.4, "rgba(90, 0, 0, 0.035)");
  bgGrad.addColorStop(1, "rgba(0, 0, 0, 0.015)");
  ctx.fillStyle = bgGrad;
  ctx.fillRect(barX, y, barWidth, barH);

  const sideGrad = ctx.createLinearGradient(barX, y, barX + barWidth, y);
  sideGrad.addColorStop(0, "rgba(255, 0, 0, 0.04)");
  sideGrad.addColorStop(0.15, "rgba(255, 0, 0, 0.01)");
  sideGrad.addColorStop(0.85, "rgba(255, 0, 0, 0.01)");
  sideGrad.addColorStop(1, "rgba(255, 0, 0, 0.04)");
  ctx.fillStyle = sideGrad;
  ctx.fillRect(barX, y, barWidth, barH);

  const gloss = ctx.createLinearGradient(barX, y, barX, y + barH);
  gloss.addColorStop(0, "rgba(255, 255, 255, 0.1)");
  gloss.addColorStop(0.1, "rgba(255, 230, 220, 0.04)");
  gloss.addColorStop(0.38, "rgba(255, 255, 255, 0)");
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = gloss;
  ctx.fillRect(barX, y, barWidth, barH);
  ctx.globalAlpha = 1;

  metrics.forEach((metric, index) => {
    const colCx = barX + colW * index + colW / 2;
    const icon = metricImages[index];

    if (index > 0) {
      drawStatDivider(ctx, barX + colW * index, y, barH);
    }

    const rowY = y + barH / 2;
    const parts = [];

    if (icon?.naturalWidth) {
      const { w, h, offsetY } = measureBrandIcon(icon, metric.brandKey);
      parts.push({ type: "icon", w, h, offsetY, img: icon });
    }
    if (metric.inlineLabel) {
      parts.push({ type: "label", text: metric.inlineLabel });
    }
    parts.push({ type: "value", text: formatMetricValue(metric.value) });

    ctx.font = `600 ${statFontSize}px "Urbanist", sans-serif`;
    const labelWidths = parts.map((part) => {
      if (part.type === "icon") return part.w;
      if (part.type === "label") return ctx.measureText(part.text).width;
      ctx.font = `800 ${statFontSize}px "Urbanist", sans-serif`;
      const width = ctx.measureText(part.text).width;
      ctx.font = `600 ${statFontSize}px "Urbanist", sans-serif`;
      return width;
    });
    const totalW = labelWidths.reduce((a, b) => a + b, 0) + statGap * (parts.length - 1);
    let cursorX = colCx - totalW / 2;

    parts.forEach((part) => {
      if (part.type === "icon") {
        ctx.drawImage(
          part.img,
          cursorX,
          rowY - part.h / 2 + part.offsetY,
          part.w,
          part.h
        );
        cursorX += part.w + statGap;
      } else if (part.type === "label") {
        ctx.font = `600 ${statFontSize}px "Urbanist", sans-serif`;
        ctx.fillStyle = "rgba(255, 252, 250, 0.95)";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(part.text, cursorX, rowY);
        cursorX += ctx.measureText(part.text).width + statGap;
      } else {
        ctx.font = `800 ${statFontSize}px "Urbanist", sans-serif`;
        ctx.fillStyle = "#fffefd";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.shadowColor = "rgba(255, 255, 255, 0.14)";
        ctx.shadowBlur = s(10);
        ctx.fillText(part.text, cursorX, rowY);
        ctx.shadowBlur = 0;
        cursorX += ctx.measureText(part.text).width;
      }
    });
  });

  ctx.restore();

  return barH;
}

/**
 * Renders a PNG share card matching the Your PNL tab hero-circle on the site.
 * @param {ReturnType<typeof import('./buildPnlSharePayload').buildPnlSharePayload>} data
 * @returns {Promise<Blob>}
 */
export async function generatePnlOgImage(data) {
  await ensureFonts();

  const preloadedAvatar =
    data.profileImagePreloaded?.naturalWidth > 0
      ? data.profileImagePreloaded
      : null;

  const pnlNeg = Number(data.pnlEth) < 0;
  const roiNeg = Number(data.roiPct) < 0;

  const [
    charactersImg,
    ringImg,
    bgImg,
    ethImg,
    starImg,
    avatarImg,
    fallbackAvatarImg,
    frameImg,
    ...metricImgs
  ] = await Promise.all([
    loadImageAsset(pnlCharacters),
    loadImageAsset(circleRing),
    loadImageAsset(pageBackground),
    loadImageAsset(ethLogo),
    loadImageAsset(starGold),
    preloadedAvatar
      ? Promise.resolve(preloadedAvatar)
      : loadProfileImageForCanvas(data.profileSrc, data.profileImageEl),
    loadImageAsset(defaultAvatar),
    loadImageAsset(framePng),
    ...data.metrics.map((m) => loadImageAsset(m.brandIcon)),
  ]);

  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  const cx = CANVAS_W / 2;
  const circleR = CIRCLE_D / 2;
  const cy = CANVAS_H / 2;
  const circleTop = cy - circleR;

  drawPageBackground(ctx, bgImg);
  drawCircleWallpaper(ctx, bgImg, cx, cy, circleR);
  drawCharacters(ctx, charactersImg, cx, cy, circleR);
  drawRing(ctx, ringImg, cx, cy, circleR);
  drawCircleRedGlow(ctx, cx, cy, circleR);

  const avatarSize = s(128);
  const avatarTop =
    circleTop + s(LAYOUT.profileBlockTop) + s(-LAYOUT.profileBlockLift) + s(LAYOUT.avatarTranslateY);
  drawAvatar(ctx, avatarImg || fallbackAvatarImg, cx, avatarTop, avatarSize);

  const username = String(data.username || "@Trader");
  const usernameFontSize = s(LAYOUT.usernameFontSize);
  const usernameTop = avatarTop + avatarSize + s(LAYOUT.usernameGapAfterAvatar);
  ctx.font = `700 ${usernameFontSize}px "Urbanist", sans-serif`;
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.letterSpacing = `${s(0.6)}px`;
  ctx.fillText(username, cx, usernameTop);
  ctx.letterSpacing = "0px";

  const statusFontSize = s(LAYOUT.statusFontSize);
  const statusY =
    usernameTop +
    usernameFontSize +
    s(LAYOUT.profileFlexGap) +
    s(LAYOUT.statusMarginTop);
  const statusParts = [
    { text: "🏆  Top ", accent: false },
    { text: `${data.topPercent}%`, accent: true },
    { text: " NFT Trader ", accent: false },
    { text: "|", accent: false, sep: true },
    { text: " Receiving ", accent: false },
    { text: String(data.points), accent: true },
    { text: " Points ", accent: false },
  ];
  const starSize = s(11.84);
  drawStatusLine(ctx, statusParts, cx, statusY, statusFontSize, starImg, starSize);

  const headRowTop = circleTop + s(LAYOUT.contentPanelTop) + s(LAYOUT.headRowMarginTop);
  const pnlValue = formatHeroRingValue(data.pnlEth);
  const roiText = formatRoi(data.roiPct);

  const headRowBottom = drawHeadRow(ctx, {
    cx,
    rowTop: headRowTop,
    pnlValue,
    roiText,
    pnlNeg,
    roiNeg,
    ethImg,
  });

  const barY =
    headRowBottom +
    s(LAYOUT.headRowMarginBottom) +
    s(LAYOUT.statsFootMarginTop) +
    s(LAYOUT.statsGridMarginTop);
  const barWidth = circleR * 2;
  const barH = drawStatsBar(ctx, data.metrics, metricImgs, cx, barY, barWidth);

  const captionY = barY + barH + s(LAYOUT.captionMarginTop) + s(LAYOUT.captionPaddingTop);
  ctx.font = `${STATS_CAPTION.fontWeight} ${s(STATS_CAPTION.fontSize)}px "Urbanist", sans-serif`;
  ctx.fillStyle = "rgba(255, 255, 255, 0.94)";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.letterSpacing = `${s(0.23)}px`;
  ctx.fillText(data.statsCaption || "", cx, captionY);

  drawBorderFrame(ctx, frameImg);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to export image"));
      },
      "image/png",
      1
    );
  });
}

export async function generatePnlOgImageDataUrl(data) {
  const blob = await generatePnlOgImage(data);
  return URL.createObjectURL(blob);
}
